import { UserCog, Users } from 'lucide-react';
import { useCustomerJourney } from '../../context/setup';
import { ActionType, BusinessRole } from '../../context/types';
import { motion } from 'framer-motion';
import { trackGenericEvent } from '@/analytics';

export const BusinessRoleStep: React.FC = () => {
  const { state, dispatch, handleBusinessRoleMutation } = useCustomerJourney();

  const handleRoleSelection = (role: BusinessRole) => {
    dispatch({
      type: ActionType.SET_BUSINESS_ROLE,
      payload: role
    });

    // Track business role selection
    trackGenericEvent('Journey Business Role Selected', {
      role: role === BusinessRole.ADMIN_VENDOR ? 'Admin and Service Provider' : 'Admin Only'
    });

    setTimeout(() => {
      handleBusinessRoleMutation(role);
    }, 100);
  };

  const cardVariants = {
    hover: {
      boxShadow: '0 10px 15px -3px rgba(0, 114, 251, 0.15), 0 4px 6px -2px rgba(0, 114, 251, 0.1)',
      borderColor: 'rgba(0, 114, 251, 0.6)',
      transition: { duration: 0.2 }
    },
    selected: {
      borderColor: 'rgba(0, 114, 251, 1)',
      backgroundColor: 'rgba(0, 114, 251, 0.1)',
      boxShadow: '0 10px 25px -5px rgba(0, 114, 251, 0.2), 0 10px 10px -5px rgba(0, 114, 251, 0.1)'
    }
  };

  return (
    <div className="space-y-6">
      <motion.div
        className="text-center mb-6"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <h1 className="text-2xl font-bold text-gray-900">Tu rol en el negocio</h1>
      </motion.div>
      <div className="flex flex-col gap-4">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.4, delay: 0.1 }}
          whileHover="hover"
          variants={cardVariants}
          className={`p-6 border-2 rounded-lg cursor-pointer shadow-sm hover:shadow-md transition-all ${
            state.businessRole === BusinessRole.ADMIN_VENDOR
              ? 'border-blue bg-blue/10'
              : 'border-gray-300 hover:border-blue hover:bg-blue/5'
          }`}
          onClick={() => handleRoleSelection(BusinessRole.ADMIN_VENDOR)}
        >
          <div className="flex items-start gap-4">
            <div className="p-3 rounded-full bg-blue/10 text-blue">
              <Users className="h-6 w-6" />
            </div>
            <div>
              <h3 className="font-semibold text-lg text-gray-900 mb-1">
                Administro y brindo servicios en mi negocio
              </h3>
              <p className="text-gray-600">
                Además de administrar el negocio, también brindo servicios a mis clientes.
              </p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.4, delay: 0.2 }}
          whileHover="hover"
          variants={cardVariants}
          className={`p-6 border-2 rounded-lg cursor-pointer shadow-sm hover:shadow-md transition-all ${
            state.businessRole === BusinessRole.ADMIN
              ? 'border-blue bg-blue/10'
              : 'border-gray-300 hover:border-blue hover:bg-blue/5'
          }`}
          onClick={() => handleRoleSelection(BusinessRole.ADMIN)}
        >
          <div className="flex items-start gap-4">
            <div className="p-3 rounded-full bg-blue/10 text-blue">
              <UserCog className="h-6 w-6" />
            </div>
            <div>
              <h3 className="font-semibold text-lg text-gray-900 mb-1">Solo administro el negocio</h3>
              <p className="text-gray-600">
                Me encargo de la administración pero no brindo servicios directamente a los clientes.
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};
